import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import ActividadNawiChat from './ActividadNawiChat';

// Tiempo transcurrido en la unidad mas natural: minutos, horas, dias, semanas, meses o años.
function haceCuanto(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const n = (v, uno, varios) => `hace ${v} ${v === 1 ? uno : varios}`;
  if (s < 60) return 'hace instantes';
  if (s < 3600) return n(Math.floor(s / 60), 'minuto', 'minutos');
  if (s < 86400) return n(Math.floor(s / 3600), 'hora', 'horas');
  if (s < 7 * 86400) return n(Math.floor(s / 86400), 'día', 'días');
  if (s < 30 * 86400) return n(Math.floor(s / (7 * 86400)), 'semana', 'semanas');
  if (s < 365 * 86400) return n(Math.floor(s / (30 * 86400)), 'mes', 'meses');
  return n(Math.floor(s / (365 * 86400)), 'año', 'años');
}
const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const num = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('es-AR').format(v));

function ActividadNawi({ d }) {
  const sinConteo = d.chatsCount === null || d.chatsCount === undefined;
  return (
    <div className='detail-stats'>
      <div className='detail-stat'>
        <span className='detail-stat-label'>Conversaciones</span>
        <b className='detail-stat-value'>{num(d.chatsCount)}</b>
        <span className='detail-stat-sub'>{sinConteo ? 'No se pudo contar' : `${num(d.activeChatsCount)} ${d.activeChatsCount === 1 ? 'activa' : 'activas'} · ${num(d.archivedChatsCount)} ${d.archivedChatsCount === 1 ? 'archivada' : 'archivadas'}`}</span>
      </div>
      <div className='detail-stat'>
        <span className='detail-stat-label'>Preguntas</span>
        <b className='detail-stat-value'>{num(d.questionsCount)}</b>
        <span className='detail-stat-sub' title={fechaHora(d.lastQuestionAt)}>
          {d.lastQuestionAt ? `Última ${haceCuanto(d.lastQuestionAt)}` : (sinConteo ? 'No se pudo contar' : 'Nunca preguntó')}
        </span>
      </div>
      <div className='detail-stat'>
        <span className='detail-stat-label'>Inicios de sesión</span>
        <b className='detail-stat-value'>{d.loginTracked ? num(d.loginCount ?? 0) : '—'}</b>
        <span className='detail-stat-sub' title={fechaHora(d.lastLoginAt)}>
          {!d.loginTracked ? 'NAWI Chat todavía no los registra' : d.lastLoginAt ? `Último ${haceCuanto(d.lastLoginAt)}` : 'Nunca inició sesión'}
        </span>
      </div>
    </div>
  );
}

const GET_OFFICE_DETAIL = gql`
  query GetOfficeUserDetail($userId: ID!) {
    getOfficeUserDetail(userId: $userId) {
      id email username role officeEnabled mustChangePassword deactivated inNawi createdAt
      activePlayersCount
      empresas { id empresaKey email name createdAt }
    }
  }`;
const GET_NAWI_DETAIL = gql`
  query GetNawiChatUserDetail($id: ID!) {
    getNawiChatUserDetail(id: $id) {
      id email name role mustChangePassword deactivated inOffice createdAt
      activePlayersCount
      chatsCount activeChatsCount archivedChatsCount questionsCount lastQuestionAt
      loginTracked loginCount lastLoginAt
      fechasNacimiento { fecha cuenta }
      players { id name email disc dob createdAt }
    }
  }`;

// Fechas de nacimiento tal como se guardan en las fichas de los paneles: DDMMAAAA o "pendiente".
const fechaNac = (v) => {
  const s = String(v || '').trim();
  if (!s || /^pendiente$/i.test(s) || s === 'Usuario empresa') return null;
  const m = /^(\d{2})(\d{2})(\d{4})$/.exec(s);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : s;
};
function FechaNacimiento({ fichas }) {
  const xs = (fichas || []).map((f) => ({ ...f, texto: fechaNac(f.fecha) }));
  const conFecha = xs.filter((f) => f.texto);
  const distintas = [...new Set(conFecha.map((f) => f.texto))];
  if (!xs.length) return <span title="No figura como persona en ningún panel">Sin informar</span>;
  if (!conFecha.length) return <span title="Figura en un panel, pero sin fecha cargada">Pendiente</span>;
  if (distintas.length === 1) return <span title={`Según su ficha en ${conFecha.map((f) => f.cuenta).filter(Boolean).join(', ') || 'el panel'}`}>{distintas[0]}</span>;
  return <span title="Tiene fechas distintas en cada cuenta">{conFecha.map((f) => `${f.texto}${f.cuenta ? ` (${f.cuenta})` : ''}`).join(' · ')}</span>;
}

const RESET_OFFICE = gql`mutation R($userId: ID!){ resetOfficeUserPassword(userId:$userId){ ok email mailSent } }`;
const RESET_NAWI   = gql`mutation R($id: ID!){ resetNawiChatUserPassword(id:$id){ ok email mailSent } }`;
const UPDATE_ROLE  = gql`mutation U($userId: ID!, $role: String!){ updateUserRole(userId:$userId, role:$role){ id role } }`;
const TOGGLE_OFFICE= gql`mutation T($userId: ID!, $enabled: Boolean!){ toggleOfficeAccess(userId:$userId, enabled:$enabled){ id officeEnabled } }`;
const DEACT_OFFICE = gql`mutation D($userId: ID!){ deactivateOfficeUser(userId:$userId) }`;
const REACT_OFFICE = gql`mutation D($userId: ID!){ reactivateOfficeUser(userId:$userId) }`;
const DEACT_NAWI   = gql`mutation D($id: ID!){ deactivateNawiChatUser(id:$id) }`;
const REACT_NAWI   = gql`mutation D($id: ID!){ reactivateNawiChatUser(id:$id) }`;

const ROLES = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'Usuario' },
];

export default function UserDetailModal({ userId, type, currentUserId, onClose, onChanged, onFlash }) {
  const isOffice = type === 'office';
  const { data, loading, refetch } = useQuery(
    isOffice ? GET_OFFICE_DETAIL : GET_NAWI_DETAIL,
    { variables: isOffice ? { userId } : { id: userId }, fetchPolicy: 'network-only' }
  );

  const [resetOffice] = useMutation(RESET_OFFICE);
  const [resetNawi]   = useMutation(RESET_NAWI);
  const [updateRole]  = useMutation(UPDATE_ROLE);
  const [toggleOffice]= useMutation(TOGGLE_OFFICE);
  const [deactOffice] = useMutation(DEACT_OFFICE);
  const [reactOffice] = useMutation(REACT_OFFICE);
  const [deactNawi]   = useMutation(DEACT_NAWI);
  const [reactNawi]   = useMutation(REACT_NAWI);

  const [confirm, setConfirm] = useState(null); // 'reset' | 'block' | 'unblock' | null
  const [editRole, setEditRole] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vista, setVista] = useState('resumen'); // 'resumen' | 'actividad' (solo NAWI Chat)

  const d = isOffice ? data?.getOfficeUserDetail : data?.getNawiChatUserDetail;

  const after = async (msg) => { onFlash?.(msg); await refetch(); onChanged?.(); };

  const doReset = async () => {
    setBusy(true);
    try {
      const res = isOffice
        ? await resetOffice({ variables: { userId } })
        : await resetNawi({ variables: { id: userId } });
      const r = isOffice ? res.data?.resetOfficeUserPassword : res.data?.resetNawiChatUserPassword;
      if (r?.ok && r?.mailSent) {
        await after('Contraseña reseteada. Mail con clave provisoria enviado.');
        setConfirm(null);
      } else {
        onFlash?.('No se pudo enviar el mail. La contraseña no se cambió; probá de nuevo más tarde.', 'error');
      }
    } catch (e) { onFlash?.(e.message, 'error'); }
    setBusy(false);
  };

  const doRole = async (role) => {
    setBusy(true);
    try {
      await updateRole({ variables: { userId, role } });
      await after('Rol actualizado.');
      setEditRole(false);
    } catch (e) { onFlash?.(e.message, 'error'); }
    setBusy(false);
  };

  const doToggleOffice = async () => {
    setBusy(true);
    try {
      await toggleOffice({ variables: { userId, enabled: !d.officeEnabled } });
      await after(!d.officeEnabled ? 'Acceso Office habilitado.' : 'Acceso Office deshabilitado.');
    } catch (e) { onFlash?.(e.message, 'error'); }
    setBusy(false);
  };

  const doBlock = async (block) => {
    setBusy(true);
    try {
      if (isOffice) block ? await deactOffice({ variables: { userId } }) : await reactOffice({ variables: { userId } });
      else block ? await deactNawi({ variables: { id: userId } }) : await reactNawi({ variables: { id: userId } });
      await after(block ? 'Acceso bloqueado.' : 'Acceso desbloqueado.');
      setConfirm(null);
    } catch (e) { onFlash?.(e.message, 'error'); }
    setBusy(false);
  };

  const Row = ({ k, v }) => (
    <div className="detail-row"><span className="detail-label">{k}</span><span className="detail-value">{v}</span></div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-card${!isOffice && vista === 'actividad' ? ' modal-card--ancho' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
              {isOffice ? 'Usuario Office' : 'Usuario NAWI Chat'}
            </h2>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '2px 0 0' }}>{d?.email || ''}</p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {!isOffice && (
          <div className="modal-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={vista === 'resumen'} className={`modal-tab${vista === 'resumen' ? ' modal-tab--activa' : ''}`} onClick={() => setVista('resumen')}>Resumen</button>
            <button type="button" role="tab" aria-selected={vista === 'actividad'} className={`modal-tab${vista === 'actividad' ? ' modal-tab--activa' : ''}`} onClick={() => setVista('actividad')}>Actividad en NAWI Chat</button>
          </div>
        )}

        {!isOffice && vista === 'actividad' ? <ActividadNawiChat userId={userId} /> : loading || !d ? <div className="spinner" /> : (
          <>
            {!isOffice && <ActividadNawi d={d} />}
            <Row k="Email" v={d.email || '-'} />
            {!isOffice && <Row k="Nombre" v={d.name || '-'} />}
            {!isOffice && <Row k="Fecha de nacimiento" v={<FechaNacimiento fichas={d.fechasNacimiento} />} />}
            {isOffice && <Row k="Username" v={d.username || '-'} />}
            <Row k="Rol" v={(d.role || '-').toUpperCase()} />
            <Row k="Estado" v={d.deactivated ? 'Bloqueado' : 'Activo'} />
            {isOffice && <Row k="Acceso Office" v={d.officeEnabled ? 'Habilitado' : 'Bloqueado'} />}
            <Row k={isOffice ? 'También en NAWI' : 'También en Office'} v={(isOffice ? d.inNawi : d.inOffice) ? 'Sí' : 'No'} />
            <Row k="Cambio pass pendiente" v={d.mustChangePassword ? 'Sí' : 'No'} />
            <Row k="Usuarios activos en su panel" v={d.activePlayersCount ?? 0} />
            <Row k="Creado" v={d.createdAt ? new Date(d.createdAt).toLocaleString() : '-'} />

            {isOffice && d.empresas?.length > 0 && (
              <>
                <div className="detail-section-title">Empresas ({d.empresas.length})</div>
                <div className="player-scroll" style={{ maxHeight: 160 }}>
                  {d.empresas.map(e => (
                    <div key={e.id} className="player-item" style={{ cursor: 'default' }}>
                      <div className="player-info">
                        <div className="player-name">{e.empresaKey || e.name || e.email}</div>
                        <div className="player-meta">{e.email}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {!isOffice && d.players?.length > 0 && (
              <>
                <div className="detail-section-title">Usuarios activos ({d.players.length})</div>
                <div className="player-scroll" style={{ maxHeight: 180 }}>
                  {d.players.map(p => (
                    <div key={p.id} className="player-item" style={{ cursor: 'default' }}>
                      <div className="player-info">
                        <div className="player-name">{p.name || p.email}</div>
                        <div className="player-meta">{p.email}{p.disc ? ` · ${p.disc}` : ''} · {fechaNac(p.dob) ? `Nac. ${fechaNac(p.dob)}` : 'Sin fecha de nacimiento'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── Permisos / Rol (solo usuarios Office: el rol de NAWI Chat no se gestiona desde aca) ── */}
            {isOffice && (<>
            <div className="detail-section-title">Permisos</div>
            {editRole ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {ROLES.map(r => (
                  <button key={r.value} disabled={busy} className="btn-outline" onClick={() => doRole(r.value)}>{r.label}</button>
                ))}
                <button className="btn-subtle" onClick={() => setEditRole(false)}>Cancelar</button>
              </div>
            ) : (
              <button className="btn-outline" onClick={() => setEditRole(true)} style={{ marginBottom: 8 }}>Cambiar rol</button>
            )}
            <div style={{ marginBottom: 8 }}>
              <button className="btn-outline" disabled={busy} onClick={doToggleOffice}>
                {d.officeEnabled ? 'Deshabilitar acceso Office' : 'Habilitar acceso Office'}
              </button>
            </div>
            </>)}

            {/* ── Acciones críticas con confirmación ── */}
            <div className="detail-section-title">Acciones</div>
            {confirm === 'reset' ? (
              <div className="del-confirm" style={{ padding: 14, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginBottom: 8 }}>
                <p style={{ fontWeight: 700, marginBottom: 8 }}>Resetear contraseña de "{d.email}"</p>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12 }}>
                  Se generará una clave provisoria y se enviará por mail. En el próximo ingreso el sistema le pedirá definir una nueva.
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn-outline" onClick={() => setConfirm(null)}>Cancelar</button>
                  <button className="btn-accent" disabled={busy} onClick={doReset}>{busy ? 'Reseteando...' : 'Resetear y enviar mail'}</button>
                </div>
              </div>
            ) : (
              <button className="btn-accent" onClick={() => setConfirm('reset')} style={{ marginBottom: 8, width: '100%' }}>
                Resetear contraseña
              </button>
            )}

            {d.deactivated ? (
              <button className="btn-green" disabled={busy} onClick={() => doBlock(false)} style={{ width: '100%' }}>Desbloquear acceso</button>
            ) : confirm === 'block' ? (
              <div className="del-confirm" style={{ padding: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
                <p style={{ fontWeight: 700, marginBottom: 8 }}>Bloquear acceso de "{d.email}"</p>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12 }}>Sus datos se conservan. Podés desbloquearlo cuando quieras.</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn-outline" onClick={() => setConfirm(null)}>Cancelar</button>
                  <button className="btn-danger" disabled={busy} onClick={() => doBlock(true)}>Bloquear acceso</button>
                </div>
              </div>
            ) : (
              <button className="btn-danger" onClick={() => setConfirm('block')} style={{ width: '100%' }}>Bloquear acceso</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
